import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ThreeDots } from 'react-loader-spinner';
import '../AdminOrderModal/css/AdminOrderModal.css';
import '../css/AdminPanel.css';
import './css/AdminCreateOrderModal.css';
import { createAdminOrder } from '../../../Utils/RestUtils';
import { emailValidation } from '../../../Utils/ValidationUtil';
import {
  ERROR_TEXT,
  INCORRECT_EMAIL_FORMAT,
} from '../../../Utils/Constants/GlobalConstants';
import {
  CREATE_ORDER_TITLE,
  ORDER_EMAIL_PLACEHOLDER,
  CREATE_ORDER_FILL_EMAIL,
  ADD_ORDER,
  ORDER_WITHOUT_FLIGHT,
} from '../../../Utils/Constants/AdminConstants';
import AdminTourActualization from '../AdminTourActualization/AdminTourActualization';
import { ReactComponent as Close } from '../../../images/close-r.svg';

function AdminCreateOrderModal(props) {
  const {
    isVisible,
    modalNode,
    handleModalClick,
    handleOrderCreated,
    defaultEmail,
  } = props;
  const [email, setEmail] = useState('');
  // the order built from the actualized tour, null until actualized
  const [tourOrder, setTourOrder] = useState(null);
  const [message, setMessage] = useState('');
  const [isPending, setIsPending] = useState(false);

  // start with an empty form on every open
  useEffect(() => {
    if (isVisible) {
      setEmail(defaultEmail);
      setMessage('');
    }
  }, [isVisible, defaultEmail]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleEscPress = (e) => {
      if (e.key === 'Escape') {
        handleModalClick();
      }
    };

    document.addEventListener('keydown', handleEscPress, false);

    return () => {
      document.removeEventListener('keydown', handleEscPress, false);
    };
  }, [isVisible, handleModalClick]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const emailValue = email.trim();

    if (!tourOrder || isPending) {
      return;
    }

    if (emailValue.length === 0) {
      setMessage(CREATE_ORDER_FILL_EMAIL);
      return;
    }

    if (!emailValidation(emailValue)) {
      setMessage(INCORRECT_EMAIL_FORMAT);
      return;
    }

    const token = window.localStorage.getItem('accessToken');

    setMessage('');
    setIsPending(true);

    createAdminOrder({ ...tourOrder, email: emailValue }, token)
      .then((response) => {
        if (response && response.data) {
          handleOrderCreated(response.data);
        } else {
          setMessage(ERROR_TEXT);
        }
      })
      .catch(() => {
        setMessage(ERROR_TEXT);
      })
      .finally(() => {
        setIsPending(false);
      });
  };

  const renderSpinner = () => (
    <ThreeDots
      visible
      height="24"
      width="30"
      color="rgba(0, 0, 0, .5)"
      radius="5"
      ariaLabel="three-dots-loading"
    />
  );

  // portal: the modal also opens on top of the user modal
  return createPortal(
    <div
      className={`aom_container aom_top ${isVisible ? 'aom_open' : ''}`}
    >
      <div
        className="aom_modal acom_modal"
        ref={(node) => {
          modalNode.current = node;
        }}
      >
        <div className="aom_title_line">
          <div className="aom_title">{CREATE_ORDER_TITLE}</div>
          <button
            type="button"
            className="aom_close_but"
            onClick={handleModalClick}
          >
            <Close />
          </button>
        </div>
        <form
          className="aom_body acom_form custom_scroll_container"
          onSubmit={handleSubmit}
        >
          <AdminTourActualization
            isVisible={isVisible}
            handleOrderChange={setTourOrder}
            withoutFlightText={ORDER_WITHOUT_FLIGHT}
          />
          <div className="acom_line">
            <input
              className="ap_text_input acom_input"
              type="text"
              value={email}
              placeholder={ORDER_EMAIL_PLACEHOLDER}
              readOnly={defaultEmail.length > 0}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              type="submit"
              className="ap_btn btn__yellow dbm_text_16"
              disabled={!tourOrder || isPending}
            >
              {isPending ? renderSpinner() : ADD_ORDER}
            </button>
          </div>
          {message.length > 0 && (
            <div className="ap_message ap_message_error br_text_14">
              {message}
            </div>
          )}
        </form>
      </div>
    </div>,
    document.body,
  );
}

AdminCreateOrderModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  modalNode: PropTypes.instanceOf(Object).isRequired,
  handleModalClick: PropTypes.func.isRequired,
  handleOrderCreated: PropTypes.func.isRequired,
  defaultEmail: PropTypes.string,
};

AdminCreateOrderModal.defaultProps = {
  defaultEmail: '',
};

export default AdminCreateOrderModal;
